import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import type { AdminApi } from "../api/client.js";
import { Card } from "./Card.js";
import { ErrorBanner } from "./ErrorBanner.js";

// Google Calendar connect/disconnect + busy-calendar selection panels.
// Originally lived inline in CalendarConfigPage.tsx as the practice-wide
// (single implicit provider) Google connection UI; moved here so
// ProvidersPage can render one instance per provider.
//
// `providerId` is optional so the components could serve both callers:
//   - omitted: hits the practice-wide `/admin/api/calendar/google/*`
//     routes, with the exact query keys the original inline panel used
//     (HealthPage reads the same ["admin", "google-status"] key directly,
//     independent of these components, so this doesn't affect it). Task 4
//     removed CalendarConfigPage's use of these components, so nothing
//     currently calls them without a providerId — this branch is dead
//     code kept around rather than deleted outright.
//   - provided (ProvidersPage, the only current caller): hits the
//     provider-scoped `/admin/api/providers/:id/google/*` routes.
//
// The `?google=` flash-toast + invalidate-on-connect logic below is scoped
// to the legacy (no providerId) case only — rendering one provider card per
// panel would otherwise show N banners and race N navigate() calls.
// ProvidersPage owns its own single page-level banner/effect instead.

type PanelProps = { api: AdminApi; providerId?: string };

function googleStatusKey(providerId?: string) {
  return providerId
    ? (["admin", "provider-google-status", providerId] as const)
    : (["admin", "google-status"] as const);
}

function availableCalendarsKey(providerId?: string) {
  return providerId
    ? (["admin", "provider-available-calendars", providerId] as const)
    : (["admin", "available-calendars"] as const);
}

function busyCalendarsKey(providerId?: string) {
  return providerId
    ? (["admin", "provider-busy-calendars", providerId] as const)
    : (["admin", "busy-calendars"] as const);
}

function fetchStatus(api: AdminApi, providerId?: string) {
  return providerId
    ? api.getProviderGoogleStatus(providerId)
    : api.getGoogleStatus();
}

function fetchAvailable(api: AdminApi, providerId?: string) {
  return providerId
    ? api.listProviderAvailableCalendars(providerId)
    : api.listAvailableCalendars();
}

function fetchBusy(api: AdminApi, providerId?: string) {
  return providerId
    ? api.getProviderBusyCalendars(providerId)
    : api.getBusyCalendars();
}

function saveBusy(api: AdminApi, calendarIds: string[], providerId?: string) {
  return providerId
    ? api.putProviderBusyCalendars(providerId, calendarIds)
    : api.putBusyCalendars(calendarIds);
}

function startOAuth(api: AdminApi, providerId?: string) {
  return providerId
    ? api.startProviderGoogleOAuth(providerId)
    : api.startGoogleOAuth();
}

function disconnectGoogle(api: AdminApi, providerId?: string) {
  return providerId
    ? api.disconnectProviderGoogle(providerId)
    : api.disconnectGoogle();
}

export function GoogleConnectionPanel({ api, providerId }: PanelProps) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  // Legacy-only flash toast — see module comment.
  const flash = providerId ? null : params.get("google");
  const flashReason = providerId ? null : params.get("reason");

  const status = useQuery({
    queryKey: googleStatusKey(providerId),
    queryFn: () => fetchStatus(api, providerId),
  });

  useEffect(() => {
    if (!providerId && flash === "connected") {
      void queryClient.invalidateQueries({
        queryKey: googleStatusKey(providerId),
      });
      navigate(location.pathname, { replace: true });
    }
  }, [providerId, flash, queryClient, navigate, location.pathname]);

  const start = useMutation({
    mutationFn: async () => startOAuth(api, providerId),
    onSuccess: (res) => {
      window.location.href = res.url;
    },
  });

  const disconnect = useMutation({
    mutationFn: async () => disconnectGoogle(api, providerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: googleStatusKey(providerId),
      });
      void queryClient.invalidateQueries({
        queryKey: busyCalendarsKey(providerId),
      });
    },
  });

  const data = status.data;

  return (
    <Card title="Google Calendar connection">
      {flash === "connected" ? (
        <p className="mb-3 rounded-md bg-emerald-50 p-2 text-sm text-emerald-800">
          Google Calendar connected. Pick which calendars to use below.
        </p>
      ) : null}
      {flash === "error" ? (
        <ErrorBanner
          message={`Google connection failed${flashReason ? `: ${flashReason}` : ""}.`}
        />
      ) : null}

      {status.isPending ? (
        <p className="text-sm text-slate-500">Checking connection status…</p>
      ) : data?.connected ? (
        <div className="grid gap-2 text-sm">
          <p>
            Connected as{" "}
            <span className="font-medium">{data.connected_by_email}</span>
            {data.connected_at ? (
              <>
                {" "}
                on{" "}
                <span className="font-medium">
                  {new Date(data.connected_at).toLocaleString()}
                </span>
              </>
            ) : null}
            .
          </p>
          <div>
            <button
              type="button"
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
              className="mt-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              {disconnect.isPending ? "Disconnecting…" : "Disconnect"}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-2">
          <p className="text-sm text-slate-600">
            Not connected. Connect to authorize Google Calendar access; you
            can then pick which calendars feed availability.
          </p>
          <div>
            <button
              type="button"
              onClick={() => start.mutate()}
              disabled={start.isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {start.isPending ? "Redirecting…" : "Connect Google Calendar"}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

export function BusyCalendarsPanel({ api, providerId }: PanelProps) {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: googleStatusKey(providerId),
    queryFn: () => fetchStatus(api, providerId),
  });
  const connected = status.data?.connected ?? false;

  const available = useQuery({
    enabled: connected,
    queryKey: availableCalendarsKey(providerId),
    queryFn: () => fetchAvailable(api, providerId),
  });
  const busy = useQuery({
    enabled: connected,
    queryKey: busyCalendarsKey(providerId),
    queryFn: () => fetchBusy(api, providerId),
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialised, setInitialised] = useState(false);

  useEffect(() => {
    if (!initialised && busy.data) {
      setSelected(new Set(busy.data.calendars.map((c) => c.calendar_id)));
      setInitialised(true);
    }
  }, [busy.data, initialised]);

  const save = useMutation({
    mutationFn: async () => saveBusy(api, [...selected], providerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: busyCalendarsKey(providerId),
      });
    },
  });

  if (!connected) {
    return (
      <Card title="Busy calendars">
        <p className="text-sm text-slate-600">
          Connect Google Calendar first to choose which calendars feed
          availability.
        </p>
      </Card>
    );
  }

  if (available.isPending || busy.isPending) {
    return (
      <Card title="Busy calendars">
        <p className="text-sm text-slate-600">Loading your calendars…</p>
      </Card>
    );
  }

  if (available.isError) {
    return (
      <Card title="Busy calendars">
        <ErrorBanner message="Could not list calendars from Google." />
      </Card>
    );
  }

  const rows = available.data?.calendars ?? [];
  const currentSet = new Set(
    busy.data?.calendars.map((c) => c.calendar_id) ?? [],
  );
  const dirty =
    rows.some((r) => selected.has(r.id) && !currentSet.has(r.id)) ||
    [...currentSet].some((id) => !selected.has(id));

  return (
    <Card
      title="Busy calendars"
      action={
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {save.isPending ? "Saving…" : "Save selection"}
        </button>
      }
    >
      <p className="mb-3 text-sm text-slate-600">
        Events on any selected calendar block availability. Adding a
        calendar runs an initial sync (a few seconds); removing one tears
        down its push subscription and drops its cached events.
      </p>
      {save.isError ? (
        <ErrorBanner
          message={
            (save.error as Error | undefined)?.message ||
            "Could not update selection."
          }
        />
      ) : null}
      <ul className="grid gap-2">
        {rows.map((c) => {
          const isSelected = selected.has(c.id);
          const meta = busy.data?.calendars.find((b) => b.calendar_id === c.id);
          return (
            <li
              key={c.id}
              className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3"
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (e.target.checked) next.add(c.id);
                    else next.delete(c.id);
                    return next;
                  });
                }}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              <div className="grow text-sm">
                <p className="font-medium text-slate-900">
                  {c.summary}
                  {c.primary ? (
                    <span className="ml-2 text-xs text-slate-500">
                      (primary)
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-slate-500">{c.id}</p>
                {meta ? (
                  <p className="mt-1 text-xs text-slate-500">
                    {meta.initial_backfill_done
                      ? "Synced"
                      : "Initial sync running…"}
                    {meta.channel_expiration
                      ? ` • channel renews around ${new Date(meta.channel_expiration).toLocaleDateString()}`
                      : null}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
